import { usePostApply } from '@components/hooks/PostApplyProvider/index.client'
import usePostTrialApplyStepViewedEvent from '@components/hooks/usePostTrialApplyStepViewedEvent'
import Button, { buttonClassNames } from '@components/primitives/Button'
import { Icon } from '@components/primitives/Icon'
import { classMerge } from '@components/utilities/classMerge'
import { faMobilePhone } from '@fortawesome/pro-regular-svg-icons/faMobilePhone'
import { colors } from '@lib/colors/colors'
import ApiRoutes from '@lib/routes/ApiRoutes'
import PatientRoutes from '@lib/routes/PatientRoutes'
import { trackEvent } from '@lib/tracking'
import { TrackingEvents } from '@lib/tracking/TrackingEvents'
import type { ApplySuccessPageProps } from 'app/p/apply-success/[token]/[step]/page'
import { useRouter } from 'next/navigation'
import { QrCode } from 'react-qrcode-pretty'

export default function SaveStudyTeamContactStep({
  idToken,
  vcfData,
}: Pick<ApplySuccessPageProps, 'idToken' | 'vcfData'>) {
  const { advanceToNextStep } = usePostApply()
  const router = useRouter()
  usePostTrialApplyStepViewedEvent({
    trialApplicationIdToken: idToken,
    type: 'save-study-team-contact-step',
  })

  if (!vcfData) {
    advanceToNextStep()
    router.push(
      PatientRoutes.trialApplySurveyPostscreenerConsents(
        idToken,
        'screening-visit-availability',
      ),
    )
    return <></>
  }

  const phoneNumberWithPin = vcfData.vcfData.sitePhone.split('p')
  const displayPhoneNumber = phoneNumberWithPin[0]?.replace(
    /\+1(\d{3})(\d{3})(\d{4})/,
    '($1) $2-$3',
  )
  const pin =
    phoneNumberWithPin.length > 1 ? `; Ext: ${phoneNumberWithPin[1]}` : ''

  const vcfUrl = ApiRoutes.v1.callbacks.vcf(idToken)

  const handleSaveContact = () => {
    trackEvent(
      TrackingEvents.POST_TRIAL_APPLY_SAVE_STUDY_TEAM_CONTACT_BUTTON_CLICKED,
      {
        trialApplicationIdToken: idToken,
      },
    )
  }

  return (
    <div className='mx-auto grid max-w-2xl grid-cols-1 items-center gap-8'>
      <div className='mx-auto flex size-24 flex-col items-center justify-center gap-2 rounded-full bg-primary50 md:hidden'>
        <Icon className='text-primary600' icon={faMobilePhone} size='2x' />
      </div>
      <h3 className={'text-2xl font-bold text-black md:text-4xl'}>
        Save the study team to your contacts
      </h3>
      <div className='flex flex-col items-center gap-4 md:rounded-lg md:border md:border-neutral100 md:p-4'>
        <div className='hidden md:block'>
          <QrCode
            bgColor={colors.neutral50}
            bgRounded
            color={{ body: colors.primary600, eyes: colors.primary800 }}
            divider
            level='M'
            margin={0}
            padding={7}
            size={240}
            value={vcfData.vcfString}
            variant={{ body: 'standard', eyes: 'gravity' }}
          />
        </div>
        <div className='block w-full md:hidden'>
          <Button.Link
            className={classMerge(
              buttonClassNames.outline.white,
              'w-full rounded-full',
            )}
            href={vcfUrl}
            newTab
            onClick={handleSaveContact}
          >
            <Icon className='mr-2' icon={faMobilePhone} />
            Save Contact
          </Button.Link>
        </div>
        <div className='flex flex-col gap-4'>
          <p className='text-center text-lg font-bold text-neutral900 md:text-left'>
            {vcfData.vcfData.siteName}:<br className='block md:hidden' />{' '}
            <a
              className='block md:hidden'
              href={`tel:${vcfData.vcfData.sitePhone}`}
            >
              {displayPhoneNumber}
              {pin}
            </a>
            <span className='hidden md:inline'>
              {displayPhoneNumber}
              {pin}
            </span>
          </p>
          <p className='text-lg text-neutral500'>
            Add the study team to your contacts so you can easily recognize
            their calls and quickly reach out.
          </p>
        </div>
      </div>
    </div>
  )
}
